import {
  NetworkEventData,
  RCEvent,
  EventName,
  EventType,
} from "../../core/eventhub/eventmodels.js";
import { EventController } from "../../core/eventhub/eventcontroller.js";
import { RidecellCallback } from "../../core/models/ridecellcallback.js";
import { CarsharingNetworkRequests } from "../networkrequestsbuilder.js";
import { Ridecell } from "../../core/ridecell.js";

export class ScheduledRentalController {
  /**
   * Gets the available start dates for given service.
   * @param {String} service_id for which start dates need to be retrieved.
   * @returns {Strings[]} List of dates
   */
  getServiceAvailableStartDates(queryParams) {
    return new Promise((resolve, reject) => {
      const params = {
        ...queryParams,
      };

      resolve(
        this.getDates(
          params,
          CarsharingNetworkRequests.serviceAvailableStartDates
        )
      );
    });
  }

  /**
   * Gets the available start times for given service.
   * @param {String} service_id for which start dates need to be retrieved.
   * @param {String} startDate Selected start date to get start times for eg: "2020-12-21T10:10:00-08:00"
   * @param {CustomerProfileMode} profileMode Mode in which the stations need to be retrieved.
   *
   * @returns {Strings[]} List of dates
   */
  getServiceAvailableStartTimes(queryParams) {
    return new Promise((resolve, reject) => {
      const params = {
        ...queryParams,
      };

      resolve(
        this.getDates(
          params,
          CarsharingNetworkRequests.serviceAvailableStartTimes
        )
      );
    });
  }

  /**
   * Gets the available start dates for given station.
   *
   * @param {Station} station Station for which start dates need to be retrieved.
   * @param {CustomerProfileMode} profileMode Mode in which the stations need to be retrieved.
   *
   * @returns {Strings[]} List of dates
   */
  getAvailableStartDates(queryParams) {
    return new Promise((resolve, reject) => {
      const params = {
        role: "CUSTOMER",
        ...queryParams,
      };

      resolve(
        this.getDates(params, CarsharingNetworkRequests.stationStartDates)
      );
    });
  }

  /**
   * Gets the available start times for given station.
   * @param {Station} station Station for which start times need to be retrieved.
   * @param {String} startDate Selected start date to get start times for eg: "2020-12-21T10:10:00-08:00"
   * @param {CustomerProfileMode} profileMode Mode in which the stations need to be retrieved.
   *
   * @returns {Strings[]} List of dates
   */
  getAvailableStartTimes(queryParams) {
    return new Promise((resolve, reject) => {
      const params = {
        role: "CUSTOMER",
        ...queryParams,
      };

      resolve(
        this.getDates(params, CarsharingNetworkRequests.stationStartTimes)
      );
    });
  }

  /**
   * Gets the available end dates for given station.
   * @param {Station} station Station for which end dates need to be retrieved.
   * @param {String} startTime Selected fully qualified start datetime to get end dates for eg: "2020-12-21T10:10:00-08:00"
   *
   * @returns {Strings[]} List of dates
   */
  getAvailableEndDates(queryParams) {
    return new Promise((resolve, reject) => {
      const params = {
        role: "CUSTOMER",
        ...queryParams,
      };

      resolve(this.getDates(params, CarsharingNetworkRequests.stationEndDates));
    });
  }

  /**
   * Gets the available end dates for given station.
   * @param {Station} station Station for which end times need to be retrieved.
   * @param {String} startTime Selected fully qualified start datetime to get end dates for eg: "2020-12-21T10:10:00-08:00"
   * @param {String} endDate Selected end date to get end times for.
   * @param {CustomerProfileMode} profileMode Mode in which the stations need to be retrieved.
   *
   * @returns {Strings[]} List of dates
   */
  getAvailableEndTimes(queryParams) {
    return new Promise((resolve, reject) => {
      const params = {
        role: "CUSTOMER",
        ...queryParams,
      };

      resolve(this.getDates(params, CarsharingNetworkRequests.stationEndTimes));
    });
  }

  /**
   * Executes the network request to get dates.
   *
   * @param {Object} queryParams Query parameters for request.
   * @param {*} url Fully qualified URL for request.
   * @param {*} callback Callback for success and failure.
   */
  getDates(queryParams, url) {
    return new Promise((resolve, reject) => {
      const requestData = new NetworkEventData(
        EventType.GET,
        url,
        queryParams //queryData
      );

      const datesCallback = new RidecellCallback();
      datesCallback.onSuccess = (response) => {
        resolve(response);
      };
      datesCallback.onFailure = (error) => {
        reject(error);
      };
      this.sendEvent(
        new RCEvent(EventName.NETWORK, requestData, datesCallback)
      );
    });
  }

  /**
   * Gets the available vehicle types for given station.
   * @param {Station} station Station for which available vehicle types need to be retrieved.
   * @param {String} startTime Selected fully qualified start datetime to get available vehicle types for eg: "2020-12-21T10:10:00-08:00"
   * @param {String} endTime Selected fully qualified end datetime to get available vehicle types for.
   * @param {CustomerProfileMode} profileMode Mode in which the stations need to be retrieved.
   *
   * @returns {[]} List of available vehicle types.
   */
  getAvailableVehicleTypes(queryParams) {
    return new Promise((resolve, reject) => {
      const params = {
        role: "CUSTOMER",
        ...queryParams,
      };

      const requestData = new NetworkEventData(
        EventType.GET,
        CarsharingNetworkRequests.stationVehicleTypes,
        params //queryData
      );

      const typesCallback = new RidecellCallback();
      typesCallback.onSuccess = (response) => {
        resolve(response.results);
      };
      typesCallback.onFailure = (error) => {
        reject(error);
      };

      this.sendEvent(
        new RCEvent(EventName.NETWORK, requestData, typesCallback)
      );
    });
  }

  /**
   * Schedules a rental.
   *
   * @param {RidecellCallback} callback Callback for success and failure.
   *
   * @returns {ScheduledRental} Scheduled rental on success
   */
  scheduleRental(params) {
    return new Promise((resolve, reject) => {
      const data = {
        customer_id: Ridecell.cachedCustomer().id,
        ...params,
      };

      const requestData = new NetworkEventData(
        EventType.POST,
        CarsharingNetworkRequests.scheduleRental,
        null, //queryData
        data //formData
      );

      const srCallback = new RidecellCallback();
      srCallback.onSuccess = (response) => {
        resolve(response);
      };
      srCallback.onFailure = (error) => {
        reject(error);
      };

      this.sendEvent(new RCEvent(EventName.NETWORK, requestData, srCallback));
    });
  }

  /**
   * Cancels a scheduled rental.
   *
   * @param {number} scheduledRentalId Identifier for scheduled rental.
   *
   */
  cancelScheduledRental({ id, ...overrideQueryParams }) {
    return new Promise((resolve, reject) => {
      const queryParams = {
        role: "CUSTOMER",
        ...overrideQueryParams,
      };

      const requestData = new NetworkEventData(
        EventType.POST,
        CarsharingNetworkRequests.cancelScheduledRental(id),
        queryParams
      );

      const srCallback = new RidecellCallback();
      srCallback.onSuccess = (response) => {
        resolve(response);
      };
      srCallback.onFailure = (error) => {
        reject(error);
      };

      this.sendEvent(new RCEvent(EventName.NETWORK, requestData, srCallback));
    });
  }

  /**
   * Creates a scheduled rental contract (aka subscription).
   *
   * @param {Object} queryParams need to include:
   * 
   * "start_location":{
      "address":"",
      "postal_code":"",
      "city":"",
      "country":""
   },
   * "start_station":"",
   * "service_id":<#>,
   * "vehicle_type_id": <#>,
   * "start_date_time":"",
   * "end_date_time":"",
   * "vehicle_assignment_method":"",
   * "vehicle_type_leasing_entity_id": <#>,
   * "delivery_contact_details":{
      "email":"",
      "phone_number":"",
      "first_name":"",
      "last_name":""
   }
   */
  createScheduledRentalContract(formData) {
    return new Promise((resolve, reject) => {
      const requestData = new NetworkEventData(
        EventType.POST,
        CarsharingNetworkRequests.createContract(),
        null, // query params
        formData
      );

      const srCallback = new RidecellCallback();
      srCallback.onSuccess = (response) => {
        resolve(response);
      };
      srCallback.onFailure = (error) => {
        reject(error);
      };

      this.sendEvent(new RCEvent(EventName.NETWORK, requestData, srCallback));
    });
  }

  sendEvent(event) {
    EventController.addEvent(event);
  }
}
